import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL } from '../config';
import { useTenant } from '../context/TenantContext';
import { fetchWithAuth } from '../utils/fetchWithAuth';

export const STAFF_TYPE_ABBR_MAX = 12;
export const STAFF_TYPE_NAME_MAX = 80;

export const DEFAULT_STAFF_TYPES = [
    { code: 'SN', abbreviation: 'SN', display_name: 'Senior Nurse', sort_order: 1 },
    { code: 'ENA', abbreviation: 'ENA', display_name: 'Enrolled Nursing Assistant', sort_order: 2 },
    { code: 'CW', abbreviation: 'CW', display_name: 'Care Worker', sort_order: 3 },
];

export function normalizeStaffLabel(value) {
    return String(value ?? '')
        .replace(/[\u0000-\u001F\u007F]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

const StaffTypesContext = createContext(null);

function normalizeStaffTypeRow(row) {
    return {
        id: row.id,
        code: row.code,
        abbreviation: row.abbreviation || row.code,
        display_name: row.display_name || row.code,
        sort_order: row.sort_order ?? 0,
        active: row.active !== false,
    };
}

/**
 * Load tenant-configured staff type labels (SN / ENA / CW).
 * Shared via StaffTypesProvider so a rename is visible everywhere immediately.
 */
function useStaffTypesState() {
    const { currentTenant } = useTenant();
    const [staffTypes, setStaffTypes] = useState(DEFAULT_STAFF_TYPES);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const loadedTenantRef = useRef(null);

    const tenantQs = useMemo(() => {
        if (!currentTenant?.id) return '';
        return `?tenant_id=${encodeURIComponent(currentTenant.id)}`;
    }, [currentTenant?.id]);

    const refresh = useCallback(async () => {
        if (!currentTenant?.id) {
            loadedTenantRef.current = null;
            setStaffTypes(DEFAULT_STAFF_TYPES);
            return DEFAULT_STAFF_TYPES;
        }

        if (loadedTenantRef.current !== currentTenant.id) {
            setStaffTypes(DEFAULT_STAFF_TYPES);
        }

        setIsLoading(true);
        setError(null);
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/staff-types${tenantQs}`);
            if (!response.ok) {
                throw new Error(`Failed to load staff types (${response.status})`);
            }
            const data = await response.json();
            const rows = Array.isArray(data) ? data : [];
            const normalized = (rows.length > 0 ? rows : DEFAULT_STAFF_TYPES)
                .filter((row) => row.active !== false)
                .map(normalizeStaffTypeRow)
                .sort((a, b) => (a.sort_order - b.sort_order) || a.code.localeCompare(b.code));
            setStaffTypes(normalized);
            loadedTenantRef.current = currentTenant.id;
            return normalized;
        } catch (err) {
            console.error('useStaffTypes:', err);
            setError(err);
            setStaffTypes((prev) => (prev.some((row) => row.id) ? prev : DEFAULT_STAFF_TYPES));
            return DEFAULT_STAFF_TYPES;
        } finally {
            setIsLoading(false);
        }
    }, [currentTenant?.id, tenantQs]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const byCode = useMemo(() => {
        const map = {};
        staffTypes.forEach((type) => {
            map[type.code] = type;
        });
        return map;
    }, [staffTypes]);

    const getAbbreviation = useCallback(
        (code) => byCode[code]?.abbreviation || code,
        [byCode]
    );

    const getDisplayName = useCallback(
        (code) => byCode[code]?.display_name || byCode[code]?.abbreviation || code,
        [byCode]
    );

    const updateStaffType = useCallback(
        async (id, { abbreviation, display_name }) => {
            if (!currentTenant?.id || !id) {
                throw new Error('Tenant and staff type id are required');
            }
            const response = await fetchWithAuth(
                `${API_BASE_URL}/staff-types/${id}${tenantQs}`,
                {
                    method: 'PUT',
                    body: JSON.stringify({ abbreviation, display_name, tenant_id: currentTenant.id }),
                }
            );
            if (!response.ok) {
                const errBody = await response.json().catch(() => ({}));
                throw new Error(errBody.error || `Failed to update staff type (${response.status})`);
            }
            const updated = await response.json();
            setStaffTypes((prev) =>
                prev.map((row) =>
                    row.id === updated.id
                        ? {
                              ...row,
                              abbreviation: updated.abbreviation,
                              display_name: updated.display_name,
                          }
                        : row
                )
            );
            return updated;
        },
        [currentTenant?.id, tenantQs]
    );

    return {
        staffTypes,
        byCode,
        isLoading,
        error,
        refresh,
        getAbbreviation,
        getDisplayName,
        updateStaffType,
        tenantQs,
    };
}

export function StaffTypesProvider({ children }) {
    const value = useStaffTypesState();
    return (
        <StaffTypesContext.Provider value={value}>
            {children}
        </StaffTypesContext.Provider>
    );
}

export function useStaffTypes() {
    const ctx = useContext(StaffTypesContext);
    if (!ctx) {
        throw new Error('useStaffTypes must be used within StaffTypesProvider');
    }
    return ctx;
}

export default useStaffTypes;
